import {
  APPOINTMENT_CHANNEL,
  Appointment,
  MODES_OF_APT,
  QueueVcMeta,
  SupportedTeleProviders,
  TWorkflowView,
  bookedAptWhitelist,
  followupAptWhileWhitelist,
  otherAptWhitelist,
  requestedAptStatusSet,
  whitelistedAptStatus,
} from "@elixir/types";
import { useQuery } from "@tanstack/react-query";
import useQueueStore from "../../stores/queue-store";
import {
  getIdb,
  getLocalVisitByIds,
  getPatientDetails,
} from "../../utils/bridges/methods";
import { AllQueuesData, QueryQueueDate, UseAllQueuesQueryProps } from "./types";
import dateFormat from "dateformat";
import dayjs from "dayjs";
import { useMyAccessProfileV2 } from "./get-my-access-profile";
import { getFilterExpressionBasedOnAptData } from "@elixir/utils";
import mingo from "mingo";
import useGetQueueConfig from "./get-queue-config";
import { PatientDetailsWithMeta } from "../../utils/bridges/types/patient";
import { offlineAptToAppointment } from "../../controllers/appointments/utils/converter-utilts";
import { FirebaseAppointment, OfflineAppointment } from "../../firestore/types";
import getB from "../../utils/bridges/EkaBridge";

const DUMMY_PT_ID = "_dummy-pt-id";

export const DUMMY_PATIENT = {
  patient: {
    id: DUMMY_PT_ID,
    localId: DUMMY_PT_ID,
    personal: {
      name: {
        f: "",
      },
      phone: {
        c: "",
        n: "",
      },
      gender: "",

      // Only for business patients in get by mobile API
    },
  },
  visits_meta: {
    visitCount: 0,
  },
};

/**
 * @returns Query name which can be consumed by react query.
 * @param date Pass Date for which you need queue data. Pass date in all cases except when you are clearing cache.
 */
export const getAllQueuesQuery = ({
  startDate,
  endDate,
  clinicId,
  workflowViews,
}: {
  startDate?: string;
  endDate?: string;
  clinicId?: string;
  workflowViews?: TWorkflowView[];
} = {}) => {
  const query = ["all-queues"];
  if (clinicId) {
    query.push(clinicId);
  }
  if (startDate) {
    query.push(startDate);
  }

  if (endDate) {
    query.push(endDate);
  }
  if (workflowViews && workflowViews.length > 0) {
    const viewIds = workflowViews.map(
      (item) => `wf-${item.viewId}-${item.updatedAt}`
    );
    query.push(...viewIds);
  }
  return query;
};

export async function convertOfflineAppointmentToOnlineAptFormat(
  apts: OfflineAppointment[]
) {
  const patientOids = apts.map((apt) => apt.patient_oid);
  const patients = await getPatientDetails({ patientOids });
  const isNewPtTagDisabled = (
    await getB()?.getIdb()?.table("misc")?.get("bizSettings")
  )?.bizSettings?.newPtTagDisabled;

  const patientMap = new Map<string, PatientDetailsWithMeta>();

  patients?.forEach((ptDetails) => {
    const patient = ptDetails.patient;
    patientMap.set(patient.id || patient.localId, ptDetails);
  });

  const formattedApt: Appointment[] = [];

  apts.forEach((apt) => {
    let ptDetails = patientMap.get(apt.patient_oid);
    if (!ptDetails) {
      console.error(
        "Patient not found",
        apt.aid,
        apt.patient_oid,
        apt.clinic_id,
        new Date(apt.full_date).toDateString()
      );

      ptDetails = DUMMY_PATIENT;
      // return;
    }
    const { patient, visits_meta } = ptDetails;

    if (isNewPtTagDisabled) {
      apt.new_patient = undefined;
    }

    formattedApt.push(offlineAptToAppointment(apt, patient, visits_meta));
  });

  return formattedApt;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.convertOfflineAppointmentToOnlineAptFormat =
  convertOfflineAppointmentToOnlineAptFormat;
export async function getAllQueuesaa({
  clinicId,
  startDate,
  endDate,
  selectedDate,
  getVendorMeta,
  workflowViews,
}: {
  clinicId: string;
  startDate?: Date;
  endDate?: Date;
  selectedDate?: Date;
  getVendorMeta?: boolean;
  workflowViews?: TWorkflowView[];
}) {
  try {
    let formattedApt: Appointment[];

    if (selectedDate) {
      const { apts } = await getQueueFromIdb({ clinicId, date: selectedDate });
      formattedApt = await convertOfflineAppointmentToOnlineAptFormat(apts);
    } else if (startDate && endDate) {
      const dateRangeApts = await getAppointmentsInDateRange({
        clinicId,
        startDate,
        endDate,
      });

      formattedApt =
        await convertOfflineAppointmentToOnlineAptFormat(dateRangeApts);
    } else {
      throw new Error(
        "Either selectedDate or both startDate and endDate must be provided"
      );
    }

    const allQueuesData = getAllAppointmentsInAllQueuesFormat({
      apts: formattedApt,
      workflowViews,
    });

    const allQueueKeys = Object.keys(allQueuesData) as [keyof AllQueuesData];

    if (allQueueKeys.length > 0) {
      for (const queueType of allQueueKeys) {
        if (Object.prototype.hasOwnProperty.call(allQueuesData, queueType)) {
          const queue = allQueuesData[queueType];
          const localContextList = await addLocatVisitId({ list: queue });
          allQueuesData[queueType] = localContextList;
        }
      }
    }

    return allQueuesData as AllQueuesData;
  } catch (e) {
    console.error("Error in getAllQueuesaa:", e);
    return {} as AllQueuesData;
  }
}

async function getAppointmentsInDateRange({
  clinicId,
  startDate,
  endDate,
}: {
  clinicId: string;
  startDate: Date;
  endDate: Date;
}): Promise<OfflineAppointment[]> {
  const startTimestamp = startDate.getTime();
  const endTimestamp = endDate.getTime();

  const apts = await getIdb()
    .appointments.where("clinic_id")
    .equals(clinicId)
    .and((apt) => {
      return apt.full_date >= startTimestamp && apt.full_date <= endTimestamp;
    })
    .toArray();

  return apts;
}

(window as any).getAllQueuesaa = getAllQueuesaa;
export async function getQueueFromIdb({
  clinicId,
  date,
}: {
  clinicId: string;
  date: Date;
}) {
  if (clinicId === "all") {
    const clinics = getB()
      .getMe()
      .clinics.sort((a, b) => a.id.localeCompare(b.id));
    const apts = await getIdb()
      .appointments.where("full_date")
      .between(
        dayjs(date).startOf("day").unix() * 1000,
        dayjs(date).endOf("day").unix() * 1000,
        true,
        true
      )
      .filter((appointment) =>
        clinics.map((c) => c.id).includes(appointment.clinic_id)
      )
      .toArray();
    console.log("apts", clinicId, date, apts);
    return { apts };
  }
  const apts = await getIdb()
    .appointments.where("[clinic_id+full_date]")
    .between(
      [clinicId, dayjs(date).startOf("day").unix() * 1000],
      [clinicId, dayjs(date).endOf("day").unix() * 1000],
      true,
      true
    )
    .toArray();

  return { apts };
}

export function getTeleLinks({
  appointment,
}: {
  appointment: Pick<FirebaseAppointment, "aid" | "vc_meta" | "mode">;
}): QueueVcMeta | null {
  const { vc_meta, mode } = appointment;

  if (mode !== MODES_OF_APT.TELE) {
    return null;
  }

  if (!vc_meta) {
    return {
      platform: SupportedTeleProviders.eka,
    };
  }
  const vcMeta = vc_meta as QueueVcMeta;

  if (vcMeta && !vcMeta.host_link) {
    vcMeta.host_link = vcMeta.meet_link;
  }

  return vcMeta;
}

const refetchIntervalInSecs = 30;

/**
 * This function will pick date from queueStore in zustand and give you the react query response for that particular date.
 */
const useAllQueuesQuery = (
  { queueType }: UseAllQueuesQueryProps = { queueType: "all" }
) => {
  const selectedDate = useQueueStore((state) => state.selectedDate);
  const updateSelectedAppointment = useQueueStore(
    (state) => state.updateSelectedAppointment
  );
  const clinicId = useQueueStore((state) => state.clinicId);

  const { data: queueConfigData } = useGetQueueConfig();
  const { workflow_views, apt_config } = queueConfigData?.data || {};

  const { data: myAccessProfile } = useMyAccessProfileV2({ clinicId });

  return useQuery(
    getAllQueuesQuery({
      startDate: dateFormat(selectedDate, "yyyy-mm-dd"),
      clinicId: clinicId,
      workflowViews: workflow_views,
    }),
    () =>
      getAllQueuesaa({
        clinicId: clinicId,
        selectedDate,
        workflowViews: workflow_views,
        getVendorMeta: !!apt_config?.show_vendor_meta,
      }),
    {
      select: (queryData) => {
        if (queueType === "all") {
          return queryData;
        }

        return {
          [queueType]: queryData[queueType],
        } as QueryQueueDate;
      },
      staleTime: 10 * 60 * 1000,
      useErrorBoundary: false,
      enabled: !!clinicId && !!queueConfigData && !!myAccessProfile,
      refetchOnWindowFocus: "always",
      onSuccess: (data) => {
        updateSelectedAppointment(data);
      },
    }
  );
};

export default useAllQueuesQuery;

const addLocatVisitId = async ({ list }: { list: Appointment[] }) => {
  const visitIds = list.map((a) => a.aid);
  const localVisits = await getLocalVisitByIds(visitIds);
  const localContextList: LocalAppointment[] = list.map((a, index) => ({
    ...a,
    isLocalPresnt: localVisits[index] !== undefined,
    serialNum: index + 1,
    localRxId: localVisits[index]?.prescriptionId,
  }));
  return localContextList;
};

export type LocalAppointment = Appointment & {
  isLocalPresnt: boolean;
  localRxId: string;
  serialNum: number;
};

function getAllAppointmentsInAllQueuesFormat({
  apts,
  workflowViews,
}: {
  apts: Appointment[];
  workflowViews: TWorkflowView[] | undefined;
}): {
  followupAppointments: Appointment[];
  bookedAppointments: Appointment[];
  otherAppointments: Appointment[];
  requestedAppointments: Appointment[];
  [key: string]: Appointment[];
} {
  const bookedAppointments: Appointment[] = [];
  const otherAppointments: Appointment[] = [];
  const followupAppointments: Appointment[] = [];
  const requestedAppointments: Appointment[] = [];

  for (let i = 0; i < apts.length; i++) {
    const currentAppointment = apts[i];
    const aptStatus = currentAppointment.status;

    if (currentAppointment.archive || !whitelistedAptStatus.has(aptStatus)) {
      continue;
    }

    if (
      currentAppointment.channel === APPOINTMENT_CHANNEL.PATIENT_REQUESTED &&
      requestedAptStatusSet.has(aptStatus)
    ) {
      requestedAppointments.push(currentAppointment);
    }
    if (
      currentAppointment.visit_type === "FLW" &&
      followupAptWhileWhitelist.has(aptStatus)
    ) {
      followupAppointments.push(currentAppointment);
    }
    if (bookedAptWhitelist.has(aptStatus)) {
      bookedAppointments.push(currentAppointment);
    } else if (otherAptWhitelist.has(aptStatus)) {
      otherAppointments.push(currentAppointment);
    }
  }

  const activeApts = apts.filter((apt) => !apt.archive);
  const workflowAptsRecord = workflowViews?.reduce(
    (acc: Record<string, Appointment[]>, view) => {
      const currentViewApts = activeApts.reduce((acc: Appointment[], apt) => {
        const aptFilterExpression = getFilterExpressionBasedOnAptData(apt);
        const parsedViewFilterExpression = view.filterExpression || {};
        const query = new mingo.Query(parsedViewFilterExpression);
        const matched = query.test(aptFilterExpression);
        if (matched) {
          acc.push(apt);
        }
        return acc;
      }, []);
      acc[view.viewId!] = currentViewApts;
      return acc;
    },
    {}
  );

  const sortedViewRecords = workflowViews?.reduce(
    (acc: Record<string, Appointment[]>, view) => {
      const viewId = view.viewId!;
      const viewApts = workflowAptsRecord?.[viewId] || [];
      let sortedApts = viewApts;
      if (view.sortExpression && view.sortExpression.length > 0) {
        const sortCriteria = view.sortExpression.reduce((acc, obj) => {
          return { ...acc, ...obj };
        }, {});
        sortedApts = mingo.aggregate(viewApts, [
          { $sort: sortCriteria },
        ]) as unknown as Appointment[];
      }
      acc[viewId] = sortedApts;
      return acc;
    },
    {}
  );

  return {
    followupAppointments: followupAppointments.sort(startTimeBasedComparator),
    bookedAppointments: bookedAppointments.sort(startTimeBasedComparator),
    otherAppointments: otherAppointments.sort(startTimeBasedComparator),
    requestedAppointments: requestedAppointments.sort(
      createdAtTimeBasedComparator
    ),
    ...sortedViewRecords,
  };
}

function startTimeBasedComparator(a: Appointment, b: Appointment) {
  return new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
}

function createdAtTimeBasedComparator(a: Appointment, b: Appointment) {
  return new Date(a.created_at!).getTime() - new Date(b.created_at!).getTime();
}
